import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Icon } from "../../components/icons";
import { Pagination } from "../../components/Pagination";
import { Alert, Button, Card, CardHeader, EmptyState, Input, Mono, Table, TableSkeleton, Td, Th, Tag, cx } from "../../components/ui";
import { formatRelative } from "../../lib/format";
import { money } from "../../lib/money";
import { useDomainOrders, useSettleDomainOrder, useStuckDomainOrders } from "../../lib/queries";
import type { DomainMoney, DomainOrderParams, DomainsOverview, StuckDomainOrder } from "../../lib/types";
import { useDomainsTab } from "./DomainsPage";

const LIMIT = 25;

const STATUS_TONE: Record<StuckDomainOrder["status"], "neutral" | "good" | "warn" | "bad"> = {
  charged: "warn",
  completed: "good",
  reversed: "neutral",
  failed: "bad",
};

const FILTERS: { key: DomainOrderParams["status"] | ""; label: string }[] = [
  { key: "", label: "All" },
  { key: "completed", label: "Completed" },
  { key: "reversed", label: "Reversed" },
  { key: "charged", label: "Charged" },
  { key: "failed", label: "Failed" },
];

/**
 * The money side. Orders the registrar never answered first, because each
 * is a customer's credit gone with nothing to show and only a person can
 * settle it; then every order, with what it cost and what was charged, so
 * the margin on domains can be read the way it is read on everything else.
 */
export function OrdersTab() {
  const { canWrite, overview } = useDomainsTab();
  return (
    <div className="space-y-6">
      {overview && <Money totals={overview.money} />}
      <Stuck canWrite={canWrite} />
      <AllOrders />
    </div>
  );
}

/**
 * Sold, cost, margin — this month and since the start. The one product with
 * a per-unit supplier cost, so the margin is a fact rather than an estimate,
 * and the number the markup setting on the Catalogue tab is judged by.
 */
function Money({ totals }: { totals: DomainsOverview["money"] }) {
  const cell = (label: string, m: DomainMoney) => {
    const margin = m.soldMicros - m.costMicros;
    const pct = m.soldMicros > 0 ? Math.round((margin / m.soldMicros) * 100) : null;
    return (
      <div className="bg-surface px-5 py-4">
        <p className="micro">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums text-fg">{money(m.soldMicros)}</p>
        <p className="mt-1 text-xs text-fg-subtle">
          {m.orders} order{m.orders === 1 ? "" : "s"} · cost {money(m.costMicros)} · margin {money(margin)}
          {pct !== null && ` (${pct}%)`}
        </p>
      </div>
    );
  };
  return (
    <div className="grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-2">
      {cell("Sold this month", totals.monthToDate)}
      {cell("Sold all time", totals.allTime)}
    </div>
  );
}

function Stuck({ canWrite }: { canWrite: boolean }) {
  const orders = useStuckDomainOrders();
  const settle = useSettleDomainOrder();
  const rows = orders.data?.data ?? [];
  if (orders.isPending || rows.length === 0) return null;
  return (
    <Card>
      <CardHeader
        title="Needing a person"
        description="Charged, and the registrar never confirmed. Look the name up at Openprovider: if it exists, complete the order; if not, reverse it and the credit goes back."
      />
      {settle.error && <Alert>{settle.error.message}</Alert>}
      <Table
        head={
          <>
            <Th>Domain</Th>
            <Th>Organization</Th>
            <Th priority="sm">Order</Th>
            <Th priority="md">Charged</Th>
            <Th priority="md">Since</Th>
            <Th>{canWrite ? "Settle" : ""}</Th>
          </>
        }
      >
        {rows.map((order) => (
          <StuckRow key={order.id} order={order} canWrite={canWrite} settle={settle} />
        ))}
      </Table>
    </Card>
  );
}

function StuckRow({ order, canWrite, settle }: { order: StuckDomainOrder; canWrite: boolean; settle: ReturnType<typeof useSettleDomainOrder> }) {
  const [openproviderId, setOpenproviderId] = useState("");
  // The same step-up an adjustment takes: settling moves a customer's money.
  const [otp, setOtp] = useState("");
  const busy = settle.isPending && settle.variables?.orderId === order.id;
  const ready = otp.trim().length === 6;
  return (
    <tr>
      <Td>
        <Mono className="text-fg">{order.domain}</Mono>
        {order.error && <div className="mt-0.5 text-[11px] text-fg-subtle">{order.error}</div>}
      </Td>
      <Td>
        <Link to={`/organizations/${order.organization.id}`} className="text-fg hover:underline">
          {order.organization.name}
        </Link>
      </Td>
      <Td priority="sm">
        {order.kind}, {order.periodYears} yr{order.periodYears === 1 ? "" : "s"}
        <div className="mt-0.5">
          <Tag tone={STATUS_TONE[order.status]}>{order.status}</Tag>
        </div>
      </Td>
      <Td priority="md">{money(order.amountMicros)}</Td>
      <Td priority="md">{formatRelative(order.createdAt)}</Td>
      <Td>
        {canWrite && (
          <div className="flex flex-wrap items-center gap-2">
            {order.kind === "register" && (
              <div className="w-32">
                <Input value={openproviderId} onChange={(e) => setOpenproviderId(e.target.value)} placeholder="Registrar id" inputMode="numeric" />
              </div>
            )}
            <div className="w-28">
              <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Authenticator" inputMode="numeric" autoComplete="one-time-code" maxLength={6} aria-label="Authenticator code" />
            </div>
            <Button
              size="sm"
              variant="outline"
              loading={busy}
              disabled={!ready}
              onClick={() => settle.mutate({ orderId: order.id, outcome: "completed", openproviderId: openproviderId ? Number(openproviderId) : undefined, otp: otp.trim() })}
            >
              Complete
            </Button>
            <Button
              size="sm"
              variant="danger"
              loading={busy}
              disabled={!ready}
              onClick={() => {
                if (window.confirm(`Reverse the charge of ${money(order.amountMicros)} to ${order.organization.name}?`)) {
                  settle.mutate({ orderId: order.id, outcome: "reversed", otp: otp.trim() });
                }
              }}
            >
              Reverse
            </Button>
          </div>
        )}
      </Td>
    </tr>
  );
}

function AllOrders() {
  // `?orgId=` arrives from an organization's page: its orders alone, until
  // the chip is cleared.
  const [params, setParams] = useSearchParams();
  const orgId = params.get("orgId") ?? undefined;
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState<DomainOrderParams["status"] | "">("");
  const orders = useDomainOrders({ limit: LIMIT, offset, status: status || undefined, orgId });
  const rows = orders.data?.data ?? [];
  const total = orders.data?.total ?? 0;
  const marginMicros = rows.reduce((sum, row) => sum + (row.status === "completed" ? row.amountMicros - row.costMicros : 0), 0);

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              aria-pressed={status === filter.key}
              onClick={() => {
                setStatus(filter.key);
                setOffset(0);
              }}
              className={cx(
                "rounded-md px-2.5 py-1.5 text-[13px] transition-colors duration-200 ease-smooth",
                status === filter.key ? "bg-fg/8 font-medium text-fg" : "text-fg-muted hover:bg-fg/5 hover:text-fg",
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
        {orgId && rows[0] && (
          <button
            type="button"
            onClick={() => setParams({})}
            className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[12px] text-fg-muted hover:text-fg"
          >
            {rows[0].organization.name} only
            <Icon name="close" className="size-3" />
          </button>
        )}
        <span className="text-[13px] text-fg-subtle sm:ml-auto">
          {total} order{total === 1 ? "" : "s"}
          {rows.length > 0 && ` · ${money(marginMicros)} margin on this page`}
        </span>
      </div>
      {orders.error && <Alert>{orders.error.message}</Alert>}
      {orders.isPending ? (
        <TableSkeleton columns={6} />
      ) : rows.length === 0 ? (
        <EmptyState title="No orders" description="Nothing has been bought yet." />
      ) : (
        <Table
          head={
            <>
              <Th>Domain</Th>
              <Th>Organization</Th>
              <Th priority="sm">Order</Th>
              <Th priority="md">Charged</Th>
              <Th priority="lg">Cost</Th>
              <Th priority="md">When</Th>
            </>
          }
        >
          {rows.map((order) => (
            <tr key={order.id}>
              <Td>
                <Mono className="text-fg">{order.domain}</Mono>
                {order.error && <div className="mt-0.5 max-w-xs text-[11px] text-fg-subtle">{order.error}</div>}
              </Td>
              <Td>
                <Link to={`/organizations/${order.organization.id}`} className="text-fg hover:underline">
                  {order.organization.name}
                </Link>
              </Td>
              <Td priority="sm">
                {order.kind}
                {order.kind !== "restore" && `, ${order.periodYears} yr${order.periodYears === 1 ? "" : "s"}`}
                {order.automatic && <span className="text-fg-subtle"> · auto</span>}
                <div className="mt-0.5">
                  <Tag tone={STATUS_TONE[order.status]}>{order.status}</Tag>
                </div>
              </Td>
              <Td priority="md" className="tabular-nums">{money(order.amountMicros)}</Td>
              <Td priority="lg" className="tabular-nums text-fg-subtle">{money(order.costMicros)}</Td>
              <Td priority="md">{formatRelative(order.createdAt)}</Td>
            </tr>
          ))}
        </Table>
      )}
      <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
    </Card>
  );
}
